import React, { useRef, useEffect } from 'react';
import { gsap } from 'gsap';
import { reveal, registerScrollTrigger, prefersReducedMotion } from '../lib/motion';

const TheHouse = () => {
  const sectionRef = useRef(null);
  const headingRef = useRef(null);
  const subRef = useRef(null);
  const lineRef = useRef(null);

  useEffect(() => {
    registerScrollTrigger();
    const ctx = gsap.context(() => {

      reveal(headingRef.current,
        { opacity: 0, x: -80 },
        {
          opacity: 1, x: 0, duration: 1.4, ease: 'power3.out',
          scrollTrigger: { trigger: headingRef.current, start: 'top 80%', toggleActions: 'play none none none' }
        }
      );

      reveal(subRef.current,
        { opacity: 0, y: 30 },
        {
          opacity: 1, y: 0, duration: 1.2, ease: 'power2.out', delay: 0.2,
          scrollTrigger: { trigger: headingRef.current, start: 'top 75%', toggleActions: 'play none none none' }
        }
      );

      reveal(lineRef.current,
        { scaleX: 0, transformOrigin: 'left center' },
        {
          scaleX: 1, duration: 1.2, ease: 'power3.inOut',
          scrollTrigger: { trigger: headingRef.current, start: 'top 70%', toggleActions: 'play none none none' }
        }
      );

      // Background colour shift on scroll — a scrubbed tween, so it has no
      // meaningful reduced-motion equivalent; just land on the end colour.
      if (prefersReducedMotion()) {
        gsap.set(sectionRef.current, { backgroundColor: '#1A1510' });
      } else {
        gsap.to(sectionRef.current, {
          backgroundColor: '#1A1510',
          scrollTrigger: {
            trigger: sectionRef.current,
            start: 'top bottom',
            end: 'bottom top',
            scrub: 2,
          }
        });
      }

    }, sectionRef);

    return () => ctx.revert();
  }, []);

  return (
    <section
      id="house"
      ref={sectionRef}
      data-testid="the-house-section"
      className="section-pad"
      style={{ background: 'var(--black)', minHeight: '80vh', display: 'flex', alignItems: 'center' }}
    >
      <div style={{ maxWidth: '900px', margin: '0 auto', width: '100%' }}>

        <div style={{ marginBottom: '40px' }}>
          <span className="section-eyebrow">01 — The House</span>
        </div>

        <div
          ref={lineRef}
          style={{ width: '60px', height: '1px', background: 'var(--gold)', marginBottom: '48px' }}
        />

        <h2
          ref={headingRef}
          style={{
            fontFamily: 'Cinzel', fontStyle: 'normal',
            fontSize: 'clamp(32px, 5.5vw, 64px)',
            fontWeight: 400,
            letterSpacing: '4px',
            color: 'var(--cream)',
            lineHeight: 1.2,
            marginBottom: '40px',
            opacity: 0,
          }}
        >
          We didn't create fragrance.
          <br />
          <span style={{ color: 'var(--gold-light)' }}>We mastered it.</span>
        </h2>

        <p
          ref={subRef}
          style={{
            fontFamily: 'Cormorant Garamond', fontStyle: 'italic',
            fontSize: 'clamp(17px, 2vw, 22px)',
            color: 'var(--grey)',
            lineHeight: 1.9,
            maxWidth: '600px',
            opacity: 0,
            marginBottom: '48px',
          }}
        >
          Maison Vael is a Toronto-born fragrance house dedicated to the art
          of inspired scent. We take the world's most celebrated fragrances
          and translate them into accessible luxury — without compromise,
          without apology.
        </p>

        <div className="rule-row" style={{ justifyContent: 'flex-start' }}>
          <div className="rule-row-line" />
          <span className="rule-row-label">Toronto · Ontario · Est. 2025</span>
        </div>

      </div>
    </section>
  );
};

export default TheHouse;
